/**
 * chiptune-dsp-core.js — the pure 2A03 synthesis kernel, shared VERBATIM by the
 * main thread and the render worker.
 *
 * Why it was split out of chiptune-notes-engine.js (2026-08-13):
 * `render()` walked every sample of the whole song on the MAIN thread, and the
 * tool re-ran it on every preset/slider change. Prod telemetry (45 d, RU shard)
 * measured the cost as user-visible dead time:
 *
 *     button#ebPlayBtn      4690 ms average interaction delay
 *     div.eb-preset         3948 ms
 *     div.eb-preset-name    3660 ms
 *     svg#ebPlayIcon        2863 ms
 *
 * with 56 rage-clicks on the play button alone — people press, nothing happens,
 * they press again. The fix is not "optimise the loops": a 4-minute track is
 * 10.6 M samples × 4 voices no matter how tight the inner loop is. The fix is to
 * stop doing it on the thread that paints. So the kernel lives here, free of any
 * `window`/DOM reference, and `chiptune-render-worker.js` importScripts() it.
 *
 * ⚠️ Scope: attach to `self`, never `window`. `self` is defined in BOTH the
 * window and worker global scopes; `window` is absent in a worker and the
 * assignment throws on load, which surfaces as a silent worker that never
 * answers (the same trap that cost a session on the PDF worker — memory
 * reference_umd_wrapper_missed_worker_scope_2026_08_10).
 *
 * The DSP is byte-for-byte the previous implementation: same duty table, same
 * 32-step triangle, same 15-bit LFSR, same soft-clip. This file must not change
 * how the tool SOUNDS — only where the work happens. `chiptune-render-parity`
 * (tests/playwright) pins that by rendering both paths and comparing samples.
 *
 * Style: ES5 var/function, matching the rest of public/js and parseable on the
 * older Chromium builds still seen in prod.
 */
(function (scope) {
'use strict';

function midiToHz(m) { return 440 * Math.pow(2, (m - 69) / 12); }

// NES pulse duty cycles. Index maps to the tool's preset values, so presets
// keep meaning what they meant before the split.
var DUTIES = [0.5, 0.25, 0.125, 0.75];

function quantVol(v, steps) {
    if (!steps || steps < 2) return v;
    var q = Math.round(v * steps) / steps;
    return q < 0 ? 0 : (q > 1 ? 1 : q);
}

// Short attack/release ramps keep the 1-bit edges from clicking without
// softening the character — 2A03 envelopes are abrupt by design.
function envAt(tInNote, dur) {
    var atk = 0.002, rel = 0.02;
    if (tInNote < atk) return tInNote / atk;
    var left = dur - tInNote;
    if (left < rel) return Math.max(0, left / rel);
    return 1;
}

// Pitch-bend semitone offset at relative time ti, linearly interpolated between
// the note's bend points. `cur` is a 1-element cursor advanced monotonically so
// the whole note costs O(samples), not O(samples × points). Past the last point
// the value holds (a bend that ends mid-note stays put), which is how a 2A03
// slide behaves once the frequency register stops being written.
function bendSemiAt(pts, ti, cur) {
    while (cur[0] < pts.length - 1 && ti >= pts[cur[0] + 1].t) cur[0]++;
    var a = pts[cur[0]];
    if (cur[0] >= pts.length - 1) return a.semi;
    var bp = pts[cur[0] + 1];
    var span = bp.t - a.t;
    if (span <= 0) return bp.semi;
    var f = (ti - a.t) / span;
    return a.semi + (bp.semi - a.semi) * (f < 0 ? 0 : (f > 1 ? 1 : f));
}

/**
 * Should this note glide? `portamento:false` renders every note at its fixed
 * pitch — requested by a user on 2026-08-05 ("Also add the portamento ON/OFF
 * setting") after slides were introduced wholesale in 2c5d556217 with no way to
 * turn them off. A MIDI file authored with heavy bends became unusable as a
 * clean chiptune, and the only escape was not using the tool.
 */
function bendsOf(ev, portamento) {
    return (portamento === false) ? null : (ev.bends || null);
}

/**
 * Chip vibrato: a small periodic wobble of the frequency register.
 *
 * On a 2A03 the composer writes the period register every frame or two, so
 * vibrato is a stepped, not smooth, modulation — and it starts only AFTER the
 * note has settled, otherwise short notes sound seasick rather than expressive.
 * Both details are reproduced here: `VIB_DELAY` before it opens, and the
 * modulation quantised to the 60 Hz frame the hardware runs at.
 *
 * Returns a multiplier on the phase increment; depth is in semitones.
 */
var VIB_DELAY = 0.12;     // seconds before the wobble begins
var VIB_FRAME = 1 / 60;   // hardware writes the register once per frame

function vibratoMul(tInNote, depthSemi, rateHz) {
    if (!(depthSemi > 0) || tInNote < VIB_DELAY) return 1;
    var t = Math.floor((tInNote - VIB_DELAY) / VIB_FRAME) * VIB_FRAME;
    return Math.pow(2, (Math.sin(2 * Math.PI * rateHz * t) * depthSemi) / 12);
}

function renderPulse(events, out, sr, duty, volSteps, octMult, gain, portamento, vibDepth, vibRate) {
    for (var e = 0; e < events.length; e++) {
        var ev    = events[e];
        var freq  = midiToHz(ev.midi) * octMult;
        if (!(freq > 15 && freq < sr / 2)) continue;
        var start = Math.floor(ev.start * sr);
        var len   = Math.floor(ev.dur * sr);
        var phase = 0;
        var vol   = quantVol(ev.amp, volSteps) * gain;
        var bends = bendsOf(ev, portamento), cur = bends ? [0] : null;
        var inc = freq / sr;

        /* Arpeggio: precompute one phase increment per chord tone and switch
           between them on a sample counter. The envelope is NOT restarted at a
           step — the 2A03 only rewrites the frequency register, and re-attacking
           every 33 ms would turn a chord into a stutter. Precomputing also keeps
           the inner loop free of pow(). */
        var arp = ev.arp, arpInc = null, arpSamples = 0, arpIdx = 0, arpLeft = 0;
        if (arp && arp.pitches && arp.pitches.length > 1) {
            arpInc = new Float64Array(arp.pitches.length);
            var ok = true;
            for (var k = 0; k < arp.pitches.length; k++) {
                var f = midiToHz(arp.pitches[k]) * octMult;
                if (!(f > 15 && f < sr / 2)) { ok = false; break; }
                arpInc[k] = f / sr;
            }
            if (!ok) { arpInc = null; }
            else {
                arpSamples = Math.max(1, Math.round((arp.stepSec || (2 / 60)) * sr));
                arpLeft = arpSamples;
                inc = arpInc[0];
            }
        }

        for (var i = 0; i < len; i++) {
            var idx = start + i;
            if (idx < 0 || idx >= out.length) continue;
            if (arpInc) {
                if (--arpLeft <= 0) {
                    arpIdx = (arpIdx + 1) % arpInc.length;
                    arpLeft = arpSamples;
                }
                inc = arpInc[arpIdx];
            } else if (bends) {
                inc = (freq * Math.pow(2, bendSemiAt(bends, i / sr, cur) / 12)) / sr;
            }
            // Vibrato multiplies whatever increment the note is already using,
            // so it composes with a slide or an arpeggio instead of fighting it.
            var incNow = vibDepth ? inc * vibratoMul(i / sr, vibDepth, vibRate) : inc;
            phase += incNow;
            if (phase >= 1) phase -= Math.floor(phase);
            var s = phase < duty ? 1 : -1;
            out[idx] += s * vol * envAt(i / sr, ev.dur);
        }
    }
}

function renderTriangle(events, out, sr, octMult, gain, portamento, vibDepth, vibRate) {
    for (var e = 0; e < events.length; e++) {
        var ev   = events[e];
        var freq = midiToHz(ev.midi) * octMult;
        if (!(freq > 15 && freq < sr / 2)) continue;
        var start = Math.floor(ev.start * sr);
        var len   = Math.floor(ev.dur * sr);
        var phase = 0;
        var bends = bendsOf(ev, portamento), cur = bends ? [0] : null;
        var inc = freq / sr;
        for (var i = 0; i < len; i++) {
            var idx = start + i;
            if (idx < 0 || idx >= out.length) continue;
            if (bends) inc = (freq * Math.pow(2, bendSemiAt(bends, i / sr, cur) / 12)) / sr;
            phase += vibDepth ? inc * vibratoMul(i / sr, vibDepth * 0.5, vibRate) : inc;
            if (phase >= 1) phase -= Math.floor(phase);
            // 32-step quantised triangle, exactly as the 2A03 generates it.
            var step = Math.floor(phase * 32);
            var s = step < 16 ? (step / 8 - 1) : ((31 - step) / 8 - 1);
            // The NES triangle has no volume envelope — only a gate. Keep a
            // tiny ramp to avoid clicks at note boundaries.
            out[idx] += s * gain * envAt(i / sr, ev.dur);
        }
    }
}

function renderNoise(hits, out, sr, level, gain) {
    if (!level) return;
    var lfsr = 0x7FFF;
    for (var h = 0; h < hits.length; h++) {
        var hit  = hits[h];
        var isKick = hit.kind === 'kick';
        // Kick → slow LFSR clock (low, thumpy) + longer decay.
        // Hat/snare → fast clock (bright) + very short decay.
        var clockHz = isKick ? 1800 : 12000;
        var decay   = isKick ? 0.14 : 0.05;
        var start   = Math.floor(hit.time * sr);
        var len     = Math.floor(decay * sr);
        var inc     = clockHz / sr;
        var nPhase  = 0;
        /* ⚠️ `strength` может отсутствовать, и цена этому — НЕ тихие ударные, а
           NaN во ВСЁМ миксе: `out[idx] += NaN` заражает сэмплы навсегда, дальше
           NaN проходит софт-клип и уезжает в файл. Детектор
           (detectPercussionPcm) поле ставит всегда, но он не единственный
           возможный источник — MIDI-путь экспорта уже защищался тем же
           дефолтом (`perc[d].strength || 0.7`), а рендер верил на слово.
           Найдено сторожем каналов: соло ударных дало rms=NaN. */
        var amp     = (hit.strength > 0 ? hit.strength : 0.7) * level * gain;
        for (var i = 0; i < len; i++) {
            var idx = start + i;
            if (idx < 0 || idx >= out.length) continue;
            nPhase += inc;
            while (nPhase >= 1) {
                nPhase -= 1;
                var bit = ((lfsr >> 0) ^ (lfsr >> 1)) & 1;
                lfsr = (lfsr >> 1) | (bit << 14);
            }
            var s = (lfsr & 1) ? 1 : -1;
            var env = 1 - (i / len);
            out[idx] += s * amp * env * env;
        }
    }
}

/**
 * Percussion onsets for the noise channel.
 *
 * Deliberately NOT a model: two one-pole filters split the signal into a low
 * band (kick) and a high band (snare/hat), and we take peaks of the positive
 * energy difference in each. Cheap, dependency-free, and good enough to give
 * the render a recognisable backbeat — which is most of what makes a chiptune
 * read as "the song" rather than "a melody floating in space".
 *
 * Takes raw Float32Array + sampleRate (not an AudioBuffer) so it runs in the
 * worker, where AudioBuffer does not exist.
 */
function detectPercussionPcm(data, sr, opts) {
    opts = opts || {};
    var win  = 256;
    var minGapSec = opts.minGapSec || 0.06;

    var lowState = 0, highState = 0;
    // One-pole coefficients: ~150 Hz low-pass, ~3 kHz high-pass split.
    var aLow  = Math.exp(-2 * Math.PI * 150 / sr);
    var aHigh = Math.exp(-2 * Math.PI * 3000 / sr);

    var frames = Math.floor(data.length / win);
    var loE = new Float32Array(frames), hiE = new Float32Array(frames);
    var idx = 0;
    for (var f = 0; f < frames; f++) {
        var sumLo = 0, sumHi = 0;
        for (var i = 0; i < win; i++) {
            var x = data[idx++];
            lowState  = x * (1 - aLow) + lowState * aLow;         // low-passed
            highState = x * (1 - aHigh) + highState * aHigh;      // low-passed @3k
            var hp = x - highState;                               // → high band
            sumLo += lowState * lowState;
            sumHi += hp * hp;
        }
        loE[f] = Math.sqrt(sumLo / win);
        hiE[f] = Math.sqrt(sumHi / win);
    }

    function peaks(env, kind) {
        var hits = [];
        // Adaptive threshold: mean + 1.4σ over the whole envelope. A fixed
        // threshold either misses quiet drums or fires on every hi-hat wash.
        var sum = 0;
        for (var i = 0; i < env.length; i++) sum += env[i];
        var mean = env.length ? sum / env.length : 0;
        var varSum = 0;
        for (var v = 0; v < env.length; v++) { var d = env[v] - mean; varSum += d * d; }
        var sd = env.length ? Math.sqrt(varSum / env.length) : 0;
        var thr = mean + sd * 1.4;
        var lastT = -1;
        for (var p = 1; p < env.length - 1; p++) {
            var flux = env[p] - env[p - 1];
            if (env[p] > thr && flux > 0 && env[p] >= env[p + 1]) {
                var t = p * win / sr;
                if (t - lastT < minGapSec) continue;
                lastT = t;
                hits.push({ time: t, strength: Math.min(1, (env[p] - thr) / (sd || 1) * 0.5 + 0.4), kind: kind });
            }
        }
        return hits;
    }

    var all = peaks(loE, 'kick').concat(peaks(hiE, 'hat'));
    all.sort(function (a, b) { return a.time - b.time; });
    return all;
}

/**
 * renderMix(voices, opts) → Float32Array (mono mix, soft-clipped)
 *
 * voices: { lead, harmony, bass } — already allocated + monophonised by the
 *         caller (that step needs __atnClean and stays on the main thread; it
 *         is O(notes), i.e. microseconds, not the bottleneck).
 * opts:   length, sampleRate, waveType, volumeSteps, octaveShift, noiseLevel,
 *         percussion, portamento, onProgress(pct)
 */
function renderMix(voices, opts) {
    opts = opts || {};
    var sr      = opts.sampleRate || 44100;
    var length  = opts.length | 0;
    if (!length) throw new Error('chiptune_render_no_duration');

    var mix     = new Float32Array(length);
    var duty    = DUTIES[Math.max(0, Math.min(DUTIES.length - 1, opts.waveType | 0))];
    var steps   = opts.volumeSteps != null ? opts.volumeSteps : 16;
    var octMult = Math.pow(2, opts.octaveShift || 0);
    var porta   = opts.portamento !== false;
    // Vibrato is a taste control, so it defaults OFF (0). Depth arrives in
    // semitones; 0.35 is roughly what NES leads used.
    var vibDepth = opts.vibrato > 0 ? Math.min(2, opts.vibrato) : 0;
    var vibRate  = opts.vibratoRate > 0 ? opts.vibratoRate : 6;
    var prog    = typeof opts.onProgress === 'function' ? opts.onProgress : null;

    /* Маска каналов. Весь смысл этого инструмента — что песня РАЗОБРАНА по
       голосам 2A03: мелодия на пульс 1, аккомпанемент на пульс 2, бас на
       треугольник, ударные на шум. До этого разбор был только на словах: в
       интерфейсе его нельзя было ни услышать, ни проверить. Выключенный канал
       просто НЕ РЕНДЕРИТСЯ — это и глушение, и экономия работы, а не
       умножение на ноль после неё.
       ⚠️ `!== false`, а не `!!`: отсутствие маски обязано означать «все
       включены», иначе старый вызов без opts.mute отдал бы тишину. */
    var mute = opts.mute || {};
    var onLead = mute.lead !== true, onHarm = mute.harmony !== true,
        onBass = mute.bass !== true, onPerc = mute.perc !== true;

    // Channel balance roughly follows the 2A03's own mix: the pulses carry
    // the tune, the triangle sits under them, drums stay out of the way.
    if (prog) prog(5);
    if (onLead) renderPulse(voices.lead || [],    mix, sr, duty,      steps, octMult, 0.26, porta, vibDepth, vibRate);
    if (prog) prog(35);
    /* Pulse 2 carries the accompaniment. With arpeggio on it plays the chord
       the way a 2A03 does — one voice cycling through the tones; with it off
       it falls back to the flattened line, where a note that overlaps another
       simply cuts it short. */
    var harmony = (opts.arpeggio !== false && voices.harmonyRaw && voices.harmonyRaw.length)
        ? arpeggiate(voices.harmonyRaw, opts.arpStepSec)
        : (voices.harmony || []);
    if (onHarm) renderPulse(harmony, mix, sr, DUTIES[1], steps, octMult, 0.18, porta, vibDepth, vibRate);
    if (prog) prog(60);
    if (onBass) renderTriangle(voices.bass || [], mix, sr, octMult, 0.26, porta, vibDepth, vibRate);
    if (prog) prog(80);
    if (onPerc) renderNoise(opts.percussion || [], mix, sr, opts.noiseLevel != null ? opts.noiseLevel : 0.5, 0.5);
    if (prog) prog(92);

    // Soft-clip rather than hard-clip: overlapping channels can sum past 1.0
    // and a hard clip on square waves sounds like a broken decoder.
    for (var i = 0; i < length; i++) {
        var v = mix[i];
        mix[i] = v > 1 ? 1 : (v < -1 ? -1 : v - (v * v * v) / 3 * 0.25);
    }
    if (prog) prog(100);

    return mix;
}

// ── Voicing ────────────────────────────────────
// Moved here from chiptune-notes-engine.js so the worker can voice notes too.
// allocate() costs 597 ms on a 4-minute transcription (measured) and, crucially,
// does NOT depend on any render parameter — `opts` is accepted and never read.
// The old code re-ran it inside every render(), so every preset click paid for a
// re-voicing whose result could not possibly differ. It now runs once per note
// set, in the worker.
// Uses self.__atnClean.extractMelody when present (importScripts'd alongside).

// ── Musical helpers ────────────────────────────────────────────────────

// Shift a bend curve's times by deltaT so a curve captured relative to a source
// note's onset lines up with a differently-timed lead event. bendSemiAt clamps,
// so points that land at negative t just hold before the note begins.
function rebaseBends(bends, deltaT){
    if (!bends || !deltaT) return bends;
    var out = [];
    for (var i = 0; i < bends.length; i++) out.push({ t: bends[i].t + deltaT, semi: bends[i].semi });
    return out;
}

function noteKey(n){
    return (Math.round((n.startTimeSeconds || 0) * 1000)) + ':' + (n.pitchMidi | 0);
}

// Resolve overlaps inside ONE monophonic channel: a 2A03 channel plays a
// single note at a time, so a note that starts while another is sounding
// truncates it. Notes are expected sorted by start time.
/**
 * Turn overlapping notes on ONE channel into a chip arpeggio instead of
 * throwing them away.
 *
 * A 2A03 has two pulse voices, so a chord is physically impossible. Every NES
 * composer solved this the same way: keep one voice sounding and rewrite its
 * frequency register every frame or two, cycling through the chord. The ear
 * fuses it into a chord — it is THE recognisable sound of the era.
 *
 * What we did instead: monophonize() truncated the earlier note whenever a
 * second one started. Measured on ordinary chordal material (triads + bass +
 * melody, 128 notes): 12% of notes discarded outright and **40% of the
 * sounding time lost** — 150.4 s offered, 90.7 s voiced. The chords were not
 * simplified, they were deleted.
 *
 * Emits ONE event per overlap region carrying an `arp` cycle rather than a
 * stream of 33 ms notes, because the hardware does not re-trigger the
 * envelope on an arpeggio step — only the pitch changes. Chopping it into
 * separate events would re-apply the 2 ms attack / 20 ms release to every
 * step, which on a 33 ms step is almost the whole note: a stutter, not a chord.
 */
function arpeggiate(events, stepSec){
    if (!events.length) return [];
    var step = stepSec > 0 ? stepSec : (2 / 60);   // 2 frames @60 Hz, the classic rate

    // Boundaries where the sounding set can change.
    var bounds = [];
    for (var i = 0; i < events.length; i++) {
        bounds.push(events[i].start);
        bounds.push(events[i].start + events[i].dur);
    }
    bounds.sort(function(a, b){ return a - b; });

    var out = [];
    for (var b = 0; b < bounds.length - 1; b++) {
        var t0 = bounds[b], t1 = bounds[b + 1];
        if (t1 - t0 < 1e-4) continue;

        var active = [];
        var amp = 0;
        for (var e = 0; e < events.length; e++) {
            var ev = events[e];
            if (ev.start <= t0 + 1e-6 && ev.start + ev.dur > t0 + 1e-6) {
                active.push(ev.midi);
                if (ev.amp > amp) amp = ev.amp;
            }
        }
        if (!active.length) continue;

        // Ascending, de-duplicated: a chord doubled at the unison would
        // otherwise waste cycle slots on the same pitch.
        active.sort(function(a, b){ return a - b; });
        var uniq = [active[0]];
        for (var u = 1; u < active.length; u++) if (active[u] !== uniq[uniq.length - 1]) uniq.push(active[u]);

        var last = out.length ? out[out.length - 1] : null;
        // Merge with the previous region when nothing changed, so a sustained
        // chord stays one continuous event instead of restarting every time
        // some unrelated note elsewhere crossed a boundary.
        if (last && Math.abs(last.start + last.dur - t0) < 1e-6
            && last.arp && last.arp.pitches.length === uniq.length
            && last.arp.pitches.every(function(p, k){ return p === uniq[k]; })) {
            last.dur = t1 - last.start;
            if (amp > last.amp) last.amp = amp;
            continue;
        }
        if (last && Math.abs(last.start + last.dur - t0) < 1e-6
            && !last.arp && uniq.length === 1 && last.midi === uniq[0]) {
            last.dur = t1 - last.start;
            continue;
        }

        if (uniq.length === 1) {
            out.push({ start: t0, dur: t1 - t0, midi: uniq[0], amp: amp });
        } else {
            out.push({ start: t0, dur: t1 - t0, midi: uniq[0], amp: amp,
                       arp: { pitches: uniq, stepSec: step } });
        }
    }
    return out;
}

function monophonize(events){
    var out = [];
    for (var i = 0; i < events.length; i++) {
        var e = events[i];
        if (out.length) {
            var prev = out[out.length - 1];
            var prevEnd = prev.start + prev.dur;
            if (e.start < prevEnd) prev.dur = Math.max(0.01, e.start - prev.start);
        }
        if (e.dur > 0.01) out.push(e);
    }
    return out;
}

function toEvent(n){
    var ev = {
        start: n.startTimeSeconds || 0,
        dur:   Math.max(0.03, n.durationSeconds || 0),
        midi:  n.pitchMidi | 0,
        amp:   n.amplitude != null ? Math.max(0.05, Math.min(1, n.amplitude)) : 0.7,
    };
    // Carry the pitch-bend curve (MIDI) through to the render, whichever
    // channel the note lands on. Absent on transcribed-audio notes.
    if (n.bends) ev.bends = n.bends;
    return ev;
}

/**
 * Split a note set across lead / harmony / bass.
 *
 * The lead line comes from __atnClean.extractMelody when available — it runs
 * a salience + pitch-continuity Viterbi that is far better at picking the
 * line a listener would hum than "highest note wins". Without the module we
 * fall back to a highest-note-per-frame heuristic so the engine still works.
 */
function allocate(notes, opts){
    opts = opts || {};
    var sorted = notes.slice().sort(function(a, b){
        return (a.startTimeSeconds || 0) - (b.startTimeSeconds || 0);
    });

    var melody = [];
    // `scope`, not `window`: this function now runs inside the render worker,
    // where `window` is not merely empty but UNDEFINED — a bare `window.x` read
    // throws ReferenceError. That failure mode is nasty because it is invisible:
    // prepare() rejects, renderAsync silently falls back to the synchronous
    // main-thread path, and everything still works — just as slowly as before
    // the fix. Caught here only by tracing the worker messages and seeing
    // `← error` after every `→ prepare`.
    if (scope.__atnClean && typeof scope.__atnClean.extractMelody === 'function') {
        try { melody = scope.__atnClean.extractMelody(sorted) || []; } catch(_){ melody = []; }
    }
    if (!melody.length) melody = fallbackMelody(sorted);

    // The lead channel IS the melody line — use extractMelody's output
    // DIRECTLY, do not try to match it back to the raw notes. extractMelody
    // rebuilds notes on its own 50 ms Viterbi grid, so their start times no
    // longer equal any raw note's; the old key-match (round(start*1000)+midi)
    // therefore matched almost nothing, dumping the whole tune into `harmony`
    // and leaving the lead pulse near-silent — a wall of accompaniment with no
    // melody (measured: a 16-note solo line put 11 notes on harmony, 5 on
    // lead). Taking the melody notes as-is puts the tune where a listener
    // expects it: the first pulse voice.
    var lead = [];
    for (var i = 0; i < melody.length; i++) lead.push(toEvent(melody[i]));

    // Recover pitch-bend curves onto the lead. extractMelody rebuilds note
    // objects on its own grid, so any bends on the source notes were dropped
    // when the melody was picked — and the lead pulse is exactly where a slide
    // is most audible. Re-attach by matching each lead event to a source note
    // of the same pitch whose span overlaps, RE-BASED to the lead event's start
    // (extractMelody may shift a note by up to a grid step, and the bend curve's
    // t is relative to the source onset). A no-op for transcribed audio, whose
    // notes carry no bends.
    var bentByPitch = null;
    for (var s = 0; s < sorted.length; s++) {
        if (!sorted[s].bends) continue;
        if (!bentByPitch) bentByPitch = {};
        var sp = sorted[s].pitchMidi | 0;
        (bentByPitch[sp] = bentByPitch[sp] || []).push(sorted[s]);
    }
    if (bentByPitch) {
        for (var li = 0; li < lead.length; li++) {
            if (lead[li].bends) continue;
            var cand = bentByPitch[lead[li].midi];
            if (!cand) continue;
            var ls = lead[li].start, le = ls + lead[li].dur;
            for (var ci = 0; ci < cand.length; ci++) {
                var cs = cand[ci].startTimeSeconds || 0, ce = cs + (cand[ci].durationSeconds || 0);
                if (ls < ce && le > cs) {
                    lead[li].bends = rebaseBends(cand[ci].bends, cs - ls);
                    break;
                }
            }
        }
    }

    // Bass split: an octave below the median lead pitch, clamped to a sane
    // register. Deriving it from the melody (instead of a fixed MIDI number)
    // keeps the split musical for both a deep male vocal and a piccolo lead.
    var leadPitches = [];
    for (var m = 0; m < melody.length; m++) leadPitches.push(melody[m].pitchMidi | 0);
    leadPitches.sort(function(a, b){ return a - b; });
    var medianLead = leadPitches.length ? leadPitches[leadPitches.length >> 1] : 64;
    var bassSplit  = Math.max(36, Math.min(56, medianLead - 12));

    // Index the melody by pitch → sorted [start,end] spans, so a raw note that
    // merely re-states a melody note (same pitch, overlapping time) can be kept
    // OFF the harmony channel — otherwise the accompaniment doubles the tune an
    // octave-flat and muddies it. Different-pitch notes under the melody are
    // the real harmony and stay.
    var melByPitch = {};
    for (var q = 0; q < lead.length; q++) {
        var lp = lead[q].midi;
        (melByPitch[lp] = melByPitch[lp] || []).push([lead[q].start, lead[q].start + lead[q].dur]);
    }
    function coveredByMelody(n){
        var spans = melByPitch[n.pitchMidi | 0];
        if (!spans) return false;
        var s = n.startTimeSeconds || 0, e = s + (n.durationSeconds || 0);
        for (var z = 0; z < spans.length; z++) {
            if (s < spans[z][1] && e > spans[z][0]) return true;   // overlap
        }
        return false;
    }

    var harmony = [], bass = [];
    for (var k = 0; k < sorted.length; k++) {
        var n  = sorted[k];
        if ((n.pitchMidi | 0) <= bassSplit) { bass.push(toEvent(n)); continue; }
        if (coveredByMelody(n)) continue;   // the lead already sings this
        harmony.push(toEvent(n));
    }

    // The triangle channel is the bass voice — drop anything above its
    // register that leaked in, and octave-collapse very low notes so the
    // 2A03 triangle stays audible instead of rumbling below its range.
    for (var b = 0; b < bass.length; b++) {
        while (bass[b].midi < 28) bass[b].midi += 12;
    }

    return {
        lead:    monophonize(lead),
        harmony: monophonize(harmony),
        bass:    monophonize(bass),
        // The harmony BEFORE it was flattened. Whether overlaps become an
        // arpeggio or get truncated is a render-time choice (the user can
        // toggle it), so the un-flattened set has to survive the voicing —
        // once monophonize() has run, the chord is gone and no later setting
        // can bring it back.
        harmonyRaw: harmony,
        bassSplit: bassSplit,
    };
}

// Highest-sounding-note-per-frame lead extraction. Only used when
// __atnClean isn't on the page.
function fallbackMelody(sorted){
    var out = [], hop = 0.05;
    var end = 0;
    for (var i = 0; i < sorted.length; i++) {
        var e = (sorted[i].startTimeSeconds || 0) + (sorted[i].durationSeconds || 0);
        if (e > end) end = e;
    }
    var picked = {};
    for (var t = 0; t < end; t += hop) {
        var best = null;
        for (var j = 0; j < sorted.length; j++) {
            var n = sorted[j], s = n.startTimeSeconds || 0;
            if (s > t) break;
            if (s + (n.durationSeconds || 0) < t) continue;
            if (!best || (n.pitchMidi | 0) > (best.pitchMidi | 0)) best = n;
        }
        if (best && !picked[noteKey(best)]) { picked[noteKey(best)] = true; out.push(best); }
    }
    return out;
}


/**
 * crushPcm — the OTHER thing people mean by "8-bit": degrade the signal
 * itself instead of rebuilding it from notes.
 *
 * Every competing "8-bit converter" does only this (bit depth + sample-rate
 * reduction), and it answers a different question from ours: not "play my
 * song on a NES" but "make my song sound like a 1988 sound card". Both are
 * legitimate wants, and a tool that offers only re-synthesis quietly fails
 * half the people who arrive.
 *
 * Two effects, both period-accurate to how the hardware failed:
 *   • quantisation — samples snap to 2^bits levels, which is what an 8-bit
 *     DAC did (and where the crunch comes from);
 *   • sample-and-hold decimation — the value is held between updates rather
 *     than interpolated, so it aliases exactly like a slow DAC.
 *
 * Mono in / mono out; the caller decides channel layout.
 */
function crushPcm(input, sr, opts) {
    var out = new Float32Array(input.length);
    crushInto(input, out, 0, input.length, sr, opts);
    return out;
}

/* The same lo-fi pass over [start, end), written into `output` — no allocation, so a
   long track is crushed piece by piece into its result instead of through full-length
   copies (Sentry N5, 29.09.2026: a 6-minute stereo song on an Android phone ran out of
   memory at the third copy). The hold is keyed to the ABSOLUTE index, and a piece that
   starts mid-hold re-reads its boundary sample, so any split is bit-identical to one pass. */
function crushInto(input, output, start, end, sr, opts) {
    opts = opts || {};
    var bits = Math.max(1, Math.min(16, opts.bits || 8));
    var target = Math.max(1000, Math.min(sr, opts.targetRate || 11025));
    var drive = opts.drive != null ? opts.drive : 1;

    var levels = Math.pow(2, bits) - 1;
    var step = Math.max(1, Math.round(sr / target));   // hold this many samples
    function q(x) {
        var v = x * drive;
        if (v > 1) v = 1; else if (v < -1) v = -1;
        // Quantise on the 0..1 grid so the step ladder is symmetric about
        // zero the way a unipolar DAC's is.
        return (Math.round(((v + 1) / 2) * levels) / levels) * 2 - 1;
    }

    var held = start % step === 0 ? 0 : q(input[start - start % step]);
    for (var i = start; i < end; i++) {
        if (i % step === 0) held = q(input[i]);
        output[i] = held;
    }
}

scope.ChiptuneDSP = {
    renderMix: renderMix,
    crushPcm: crushPcm,
    crushInto: crushInto,
    arpeggiate: arpeggiate,
    allocate: allocate,
    detectPercussionPcm: detectPercussionPcm,
    midiToHz: midiToHz,
    DUTIES: DUTIES,
};

})(typeof self !== 'undefined' ? self : this);
